//assets
import ppt from "assets/svg/pptSvg.svg";
import jpg from "assets/svg/jpgSvg.svg";
import fig from "assets/svg/figSvg.svg";
import mp4 from "assets/svg/mp4Svg.svg";
import docSvg from "assets/svg/docxSvg.svg";

export const isFileTypeAllowed = (file) => {
  const allowedFileTypes = [
    "application/pdf", // PDF
    "application/msword", // .doc (older Word format)
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx (Word)
    "application/vnd.ms-excel", // .xls (older Excel format)
    "text/csv", // .csv
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx (Excel)
    "application/vnd.ms-powerpoint", // .ppt (older PowerPoint format)
    "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx (PowerPoint)
  ];
  return (
    allowedFileTypes.includes(file.type) ||
    file.type.startsWith("video/") ||
    file.type.startsWith("image/")
  );
};

export const timeTextFormatter = (time) => {
  let finalText;

  if (time >= 3600) {
    const hours = Math.floor(time / 3600);
    const remainingMinutes = Math.floor((time % 3600) / 60);
    finalText = `${hours}hr${hours > 1 ? "s" : ""}${
      remainingMinutes > 0
        ? ` ${remainingMinutes}min${remainingMinutes > 1 ? "s" : ""}`
        : ""
    }`;
  } else if (time >= 60) {
    const minutes = Math.floor(time / 60);
    const remainingSeconds = time % 60;
    finalText = `${minutes}min${minutes > 1 ? "s" : ""}${
      remainingSeconds > 0
        ? ` ${remainingSeconds}sec${remainingSeconds > 1 ? "s" : ""}`
        : ""
    }`;
  } else {
    finalText = `${time > 0 ? time : 0}sec${time > 1 ? "s" : ""}`;
  }

  return finalText;
};
export const mm_ss_timeTextFormatter = (time) => {
  let finalText;

  // if (time >= 3600) {
  //   const hours = Math.floor(time / 3600);
  //   const remainingMinutes = Math.floor((time % 3600) / 60);
  //   finalText = `${hours} : ${
  //     remainingMinutes > 0
  //       ? ` ${remainingMinutes}`
  //       : ""
  //   }`;
  // } else
  if (time >= 60) {
    const minutes = Math.floor(time / 60);
    const remainingSeconds = time % 60;
    finalText = `${
      minutes.toString().length == 1 ? `0${minutes}` : minutes
    } : ${remainingSeconds > 0 ? ` ${remainingSeconds}` : ""}`;
  } else {
    finalText = `${
      time > 0
        ? `00 : ` +
          `${
            Math.floor(time).toString().length == 1
              ? `0${Math.floor(time)}`
              : Math.floor(time)
          }`
        : 0
    }`;
  }

  return finalText;
};
export const tableTimeTextFormatter = (time) => {
  let finalText;

  if (time >= 3600) {
    const hours = Math.floor(time / 3600);
    const remainingMinutes = Math.floor((time % 3600) / 60);
    finalText = `${hours.toString().length == 1 ? `0${hours}` : hours} : ${
      remainingMinutes > 0
        ? `${
            remainingMinutes.toString().length == 1
              ? `0${remainingMinutes}`
              : remainingMinutes
          } : 00`
        : "00:00"
    }`;
  } else if (time >= 60) {
    const minutes = Math.floor(time / 60);
    const remainingSeconds = time % 60;
    finalText = `00 : ${
      minutes.toString().length == 1 ? `0${minutes}` : minutes
    } : ${
      remainingSeconds > 0
        ? ` ${
            remainingSeconds.toString().length == 1
              ? `0${remainingSeconds}`
              : remainingSeconds
          }`
        : "00"
    }`;
  } else {
    finalText = `00 : 00 : ${
      time > 0 ? ` ${time.toString().length == 1 ? `0${time}` : time}` : `00`
    }`;
  }

  return finalText;
};

export const formatTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  return `${hours}:${minutes < 10 ? `0${minutes}` : minutes}:${
    remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds
  }`;
};

export const formatNumber = (value) => {
  const num = value ? Number(value) : 0;

  return num?.toLocaleString();
};

/**
 * Formats a number as currency with customizable options
 * @param {number} value - The number to format
 * @param {Object} options - Configuration options
 * @param {boolean} options.showDecimals - Whether to show decimal places (default: true)
 * @param {boolean} options.roundUp - Whether to round up to nearest integer (default: false)
 * @returns {string} Formatted currency string (e.g., "70,000.00", "7.00", "7,000")
 *
 * @example
 * formatCurrency(70000) // "70,000.00"
 * formatCurrency(70000, { showDecimals: false }) // "70,000"
 * formatCurrency(70000.67, { roundUp: true }) // "70,001.00"
 * formatCurrency(7.5, { showDecimals: false, roundUp: true }) // "8"
 */
export const formatCurrency = (value, options = {}) => {
  const { showDecimals = true, roundUp = false } = options;

  // Handle invalid inputs
  if (value === null || value === undefined || isNaN(value)) {
    return showDecimals ? "0.00" : "0";
  }

  let num = Number(value);

  // Apply rounding if requested
  if (roundUp) {
    num = Math.ceil(num);
  }

  // Format with comma separators and decimal places
  const formatted = num.toLocaleString("en-US", {
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  });

  return formatted;
};

export function trimText(text, maxLength) {
  if (text?.length > maxLength) {
    return text?.substring(0, maxLength) + "...";
  } else {
    return text;
  }
}

/**
 * Truncates a filename by showing the first few characters and the last few characters (including extension)
 * with "..." in the middle
 * @param {string} filename - The filename to truncate
 * @param {number} maxLength - The maximum total length of the string (default: 40)
 * @param {number} endChars - Number of characters to show at the end including extension (default: 15)
 * @returns {string} Truncated filename with "..." in the middle
 *
 * @example
 * truncateFileName("very_long_document_name_that_is_too_long.pdf", 40, 15)
 * // Returns: "very_long_doc...too_long.pdf"
 */
export function truncateFileName(filename, maxLength = 40, endChars = 15) {
  // Return as-is if filename is null/undefined or shorter than maxLength
  if (!filename || filename.length <= maxLength) {
    return filename;
  }

  // Calculate how many characters to show at the start
  // We subtract 3 for the "..." and endChars for the end portion
  const startChars = maxLength - endChars - 3;

  // If startChars is too small, just use the old trimText behavior
  if (startChars < 5) {
    return filename.substring(0, maxLength) + "...";
  }

  // Get the first part and last part
  const start = filename.substring(0, startChars);
  const end = filename.substring(filename.length - endChars);

  return `${start}...${end}`;
}

export const capitalizeText = (str, lower = false) =>
  (lower ? str.toLowerCase() : str).replace(/(?:^|\s|["'([{])+\S/g, (match) =>
    match.toUpperCase()
  );

// export const getInitials = (name) => {
//   // Split the name into words
//   let words = name.split(" ");

//   // Initialize an empty string for the initials
//   let initials = "";

//   // Loop through each word and take the first letter
//   for (let i = 0; i < words.length; i++) {
//     initials += words[i].charAt(0);
//   }

//   // Return the initials in uppercase
//   return initials.toUpperCase().slice(0,2);
// };

export const getInitials = (name) => {
  if (!name) return "";
  const words = name.trim().split(/\s+/);

  // If single word, take first 2 characters
  if (words.length === 1) {
    return name.slice(0, 2).toUpperCase();
  }

  // For multiple words, take first letter of each word
  let initials = "";
  for (let i = 0; i < words.length; i++) {
    initials += words[i].charAt(0);
  }

  return initials.toUpperCase().slice(0, 2);
};

export const getMIMEImg = (type) => {
  if (type === "ppt") {
    return ppt;
  }
  if (type === "jpg") {
    return jpg;
  }

  if (type === "fig") {
    return fig;
  }

  if (type === "docx") {
    return docSvg;
  }

  if (type === "mp4") {
    return mp4;
  }
};
